enum CART_CATEGORIES {
  ADOPTIONS = 'adoptions',
  ADOPTION_RETURN = 'adoption_return',
  BOARDINGS = 'boardings',
  CITATIONS = 'citations',
  CLINIC_IN = 'clinic_in',
  CLINIC_OUT = 'clinic_out',
  DOA = 'doa',
  DONATIONS = 'donations',
  OWNER_REQUESTED_EUTHANASIA = 'owner_requested_euthanasia',
  OWNER_SURRENDERS = 'owner_surrenders',
  PRODUCTS_AND_SERVICES = 'products_and_services',
  RETURNS_TO_OWNER = 'returns_to_owner',
  SEIZED = 'seized',
  SERVICE_IN = 'service_in',
  SERVICE_OUT = 'service_out',
  STRAYS = 'strays',
  TRANSFER_IN = 'transfer_in',
  WILDLIFE_IN = 'wildlife_in',
}

enum CART_CHECKOUT_METHODS {
  ADOPT = 'adopt_app',
  MANUAL = 'manual',
  MOBILE = 'mobile_checkout',
}

// Why the api refused to refresh a mobile checkout. Deliberately outside CART_SESSION_ERROR: the api
// provider swallows those and runs the cart-session recovery, while this one is handled in the modal.
enum CART_REFRESH_ERROR {
  NOT_PENDING = 'MOBILE_CHECKOUT_NOT_PENDING',
}

// What the customer chose at the donation step of a cash/check/other mobile checkout. Written by adopt.
enum CUSTOMER_CARD_CHOICE {
  CUSTOM = 'custom',
  DONATION = 'donation',
  WHOLE = 'whole',
}

enum CART_ITEM_TYPES {
  ANIMAL = 'animal',
  CITATION = 'citation',
  DONATION = 'donation',
  PRODUCT = 'product',
  SERVICE = 'service',
}

enum CART_STEPS {
  ADD_PEOPLE = 'step-add-people',
  CHECKOUT_METHOD = 'step-checkout-method',
  EQUIPMENT_RENTAL_CHECKOUT = 'step-equipment-rental-checkout',
  FOSTER_CHECKOUT = 'step-foster-checkout',
  FOSTER_EQUIPMENT_RENTAL = 'step-foster-equipment-rental',
  COMPLETE_CHECK_IN = 'step-complete-check-in',
  DONATION = 'step-donation',
  MEDICAL_COSTS = 'step-medical-costs',
  MOBILE_CHECKOUT = 'step-mobile-checkout',
  MOBILE_PROCESSING = 'step-mobile-processing',
  OUTCOME_INFO = 'step-outcome-info',
  PAYMENT_METHOD = 'step-payment-method',
  PRODUCTS_AND_SERVICES = 'products-and-services',
  PRODUCTS_INFO = 'step-products-info',
  SUMMARY = 'step-summary',
}

enum CART_TYPES {
  CITATION = 'citation',
  DONATION = 'donation',
  EQUIPMENT_RENTAL = 'equipment_rental',
  FOSTER = 'foster',
  INTAKE = 'intake',
  OUTCOME = 'outcome',
  PRODUCT = 'product',
}

enum CUSTOM_OUTCOME_TYPE {
  PRE_ADOPT = 'pre_adopt',
  OFFSITE = 'offsite',
}

enum MEDICAL_COST_TYPES {
  DIAGNOSTICS = 'diagnostics',
  EXAMS = 'exams',
  MEDICATIONS = 'medications',
  PROCEDURES = 'procedures',
  SURGERIES = 'surgeries',
  VACCINATIONS = 'vaccinations',
}

enum MEDICATION_COST_TYPES {
  PER_UNIT = 'PER UNIT',
  PER_DOSE = 'PER MEDICATION',
}

enum OUTCOMES {
  CITATION = 'citation',
  DONATION = 'donation',
  FOUND = 'found',
  LOST = 'lost',
  LOST_AND_FOUND = 'lost_found',
  OUTCOME = 'outcome',
  PRODUCT = 'product',
  REHOME = 'rehome',
}

// NOTE: Do not sort this enum
enum OUTCOMES_TYPES {
  // IN CARE
  ADOPTED = 'adopted',
  CLINIC_OUT = 'clinic_out',
  DIED_IN_CARE = 'died_in_care',
  DOA = 'doa',
  LOST = 'lost',
  OWNER_REQUESTED_EUTHANASIA = 'owner_requested_euthanasia',
  RETURN_TO_FIELD = 'return_to_field',
  RETURN_TO_OWNER = 'return_to_owner',
  SERVICE_OUT = 'service_out',
  SHELTER_EUTHANASIA = 'shelter_euthanasia',
  TRANSFER_OUT = 'transfer_out',
  WILDLIFE_OUT = 'wildlife_out',

  // LOST & FOUND
  FINDER_KEPT = 'finder_kept',
  REMAINED_WITH_FINDER = 'remained_with_finder',
  REHOMED = 'rehomed',
  REUNITED_WITH_OWNER = 'reunited_with_owner',
  SHELTER_INTAKE = 'shelter_intake',
  LOST_FOUND_RETURNED_TO_FIELD = 'lost_found_returned_to_field',
  LOST_FOUND_DECEASED = 'lost_found_deceased',
  UNKNOWN = 'unknown',

  // REHOME
  REHOME = 'rehome',
  REHOME_SHELTER_INTAKE = 'rehome_shelter_intake',
  REHOME_UNKNOWN = 'rehome_unknown',
  REHOME_DECEASED = 'rehome_deceased',
  REMAINED_WITH_OWNER = 'remained_with_owner',

  // CITATIONS
  CITATION = 'citation',

  // PRODUCTS
  PRODUCTS = 'product',

  // FOSTER
  FOSTER = 'foster',

  EQUIPMENT_RENTAL = 'equipment_rental',

  // INTAKE
  INTAKE = 'intake',

  // DONATION
  DONATIONS = 'donation',
}

enum PAYMENT_METHODS {
  CARD_READER = 'card_reader',
  CASH = 'cash',
  CHECK = 'check',
  CREDIT = 'credit_card',
  OTHER = 'other',
  SPLIT_PAYMENT = 'split_payment',
}

enum PAYMENT_STATUS {
  ACTIVE = 'active',
  CANCELED = 'canceled',
  COMPLETED = 'completed',
  DELETED = 'deleted',
  EXPIRED = 'expired',
  PAID = 'paid',
  PENDING = 'pending',
  PENDING_OFFSITE_PAYMENT = 'pending_offsite_payment',
  REVERSED = 'reversed',
}

// NOTE :: Why the API refused a cart mutation. Must match `App\Enums\CartSessionError` in api —
// the values are the contract, and useCartSessionRecovery switches on them.
enum CART_SESSION_ERROR {
  GONE = 'CART_SESSION_GONE',
  MISMATCH = 'CART_SESSION_MISMATCH',
  STATUS_CHANGED = 'CART_STATUS_CHANGED',
}

// NOTE :: Why the API refused to send a checkout link. Must match
// `AnimalCheckoutHoldService::ANIMAL_HAS_OPEN_CHECKOUT` in api. Kept OUT of CART_SESSION_ERROR on
// purpose: those codes mean "this checkout is dead, leave the page", and here the staff checkout is
// fine — one of its animals is held by another checkout.
enum CART_SEND_LINK_ERROR {
  ANIMAL_HAS_OPEN_CHECKOUT = 'ANIMAL_HAS_OPEN_CHECKOUT',
}

enum RTO_FEE_TYPES {
  DAILY = 'daily',
  FLAT = 'flat',
}

enum CART_SUMMARY_CATEGORY_KEYS {
  ADDITIONAL_FEES = 'additionalFees',
  ADOPTION_FEES = 'adoptionFees',
  CITATIONS = 'citations',
  CREDIT_CARD_FEE = 'creditCardFee',
  DISCOUNT = 'discount',
  DONATIONS = 'donations',
  MEDICAL_COSTS = 'medicalCosts',
  RETAIL = 'retail',
  RTO_FEES = 'rtoFees',
  SERVICE_FEES = 'serviceFees',
  TAX = 'tax',
}

export {
  CART_CATEGORIES,
  CART_SUMMARY_CATEGORY_KEYS,
  CART_CHECKOUT_METHODS,
  CART_ITEM_TYPES,
  CART_REFRESH_ERROR,
  CART_SEND_LINK_ERROR,
  CART_SESSION_ERROR,
  CART_STEPS,
  CART_TYPES,
  CUSTOM_OUTCOME_TYPE,
  CUSTOMER_CARD_CHOICE,
  MEDICAL_COST_TYPES,
  MEDICATION_COST_TYPES,
  OUTCOMES,
  OUTCOMES_TYPES,
  PAYMENT_METHODS,
  PAYMENT_STATUS,
  RTO_FEE_TYPES,
};
